import * as React from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Box, Button, IconButton, Modal, Typography } from "@mui/material"
import { IconSeeker, ImageSeeker } from "@ui-seeker/core"

import { imageViewer } from "./@signals"

const MIN_ZOOM = 1
const MAX_ZOOM = 8
const WHEEL_SENSITIVITY = 0.0018

const AT_REST = { scale: MIN_ZOOM, x: 0, y: 0 }

//: HLS playlists. Only Safari plays these from a plain <video>, and probing is
//: the only honest way to know: `canPlayType` answers for this browser rather
//: than for a list of extensions someone guessed.
const HLS = /\.m3u8(\?|#|$)/i


function cannotPlayHls(url) {
    if (!HLS.test(url || '')) return false
    return !document.createElement('video').canPlayType('application/vnd.apple.mpegurl')
}


/**
 * A video: the real player when the browser can manage it, a way out when not.
 *
 * Retailers serve HLS playlists, YouTube embeds and the occasional direct file.
 * Guessing wrong and rendering a dead player is worse than saying plainly that
 * this one has to open elsewhere.
 */
function VideoPane({ url }) {
    const [failed, setFailed] = useState(false)

    // Try first, fall back on a real failure. Guessing from the extension was
    // wrong in both directions: the scrapers bring `.ogg` (plays), `.m3u8`
    // (does not) and Cliplister's `/deliverVideo/<hash>` with no extension at
    // all, which a regex called unplayable and the browser plays fine.
    if (!failed && !cannotPlayHls(url)) {
        return (
            <Box
                component="video"
                src={url}
                controls
                autoPlay
                onError={() => setFailed(true)}
                sx={{ maxWidth: '100%', maxHeight: '100%', backgroundColor: '#000', borderRadius: 1 }}
            />
        )
    }

    let host = url
    try { host = new URL(url).hostname } catch { /* keep the raw string */ }

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
            p: 4, textAlign: 'center', maxWidth: 560,
        }}>
            <IconSeeker icon="film" sx={{ fontSize: 40, color: '#9ca3af' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                This browser will not play this video
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                {HLS.test(url)
                    ? 'It is an HLS playlist, which only Safari plays without a helper library.'
                    : 'The retailer refused it or serves it in a format the browser cannot open.'}
                {' '}The URL is what the scraper captured, so it is still the evidence.
            </Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af', wordBreak: 'break-all' }}>
                {host}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigator.clipboard?.writeText(url)}
                    startIcon={<IconSeeker icon="copy" sx={{ fontSize: 12 }} />}
                >
                    Copy the URL
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    startIcon={<IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 12 }} />}
                >
                    Open it
                </Button>
            </Box>
            <Typography variant="caption" sx={{ color: '#d1d5db' }}>
                A player endpoint may well show a blank tab — copy it into a tool
                that speaks the format.
            </Typography>
        </Box>
    )
}


/**
 * A gallery viewer sized to the window, with wheel zoom and drag to pan.
 *
 * Deliberately NOT the app-wide `@PageWrapper/ImageModal`: that one renders at
 * natural size and knows nothing about sets of images.
 *
 * Two things this gets right that the obvious implementation does not:
 *
 * * **Zoom follows the pointer.** Scaling around the container's centre means
 *   the detail you are pointing at slides away as you zoom, so you end up
 *   hunting for it. The offset is corrected on every step to keep whatever is
 *   under the cursor under the cursor.
 * * **Panning is a drag, not a scrollbar.** A zoomed image in an `overflow:
 *   auto` box can only be moved with the scrollbars, which is unusable at 225 %.
 */
export default function ScrapingDataReviewImageViewer() {
    const data = imageViewer.value
    const [index, setIndex] = useState(0)
    const [view, setView] = useState(AT_REST)
    const [direction, setDirection] = useState(1)

    const stageRef = useRef(null)
    const dragRef = useRef(null)
    // Only a move between images animates. Opening the viewer must not: that
    // played the same slide every time an image was shown, which read as a
    // tic rather than as direction.
    const navigatedRef = useRef(false)

    const urls = data?.urls || []
    const count = urls.length
    const zoomed = view.scale > MIN_ZOOM

    // A new set (or a new starting image) resets position and zoom.
    useEffect(() => {
        if (!data) return
        setIndex(data.index || 0)
        setView(AT_REST)
        navigatedRef.current = false
    }, [data])

    const go = useCallback((delta) => {
        if (count < 2) return
        navigatedRef.current = true
        setDirection(delta)
        setIndex((current) => (current + delta + count) % count)
        setView(AT_REST)
    }, [count])

    const zoomAt = useCallback((factor, pointer) => {
        setView((current) => {
            const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, current.scale * factor))
            if (next === current.scale) return current
            if (next === MIN_ZOOM) return AT_REST

            // Keep the point under the cursor fixed: the offset has to absorb
            // the difference between where that point was and where the new
            // scale would put it.
            const ratio = next / current.scale
            return {
                scale: next,
                x: pointer.x - (pointer.x - current.x) * ratio,
                y: pointer.y - (pointer.y - current.y) * ratio,
            }
        })
    }, [])

    const pointerIn = (event) => {
        const rect = stageRef.current?.getBoundingClientRect()
        if (!rect) return { x: 0, y: 0 }
        return {
            x: event.clientX - rect.left - rect.width / 2,
            y: event.clientY - rect.top - rect.height / 2,
        }
    }

    const handleWheel = (event) => {
        zoomAt(Math.exp(-event.deltaY * WHEEL_SENSITIVITY), pointerIn(event))
    }

    const handleMouseDown = (event) => {
        if (!zoomed) return
        event.preventDefault()
        dragRef.current = { startX: event.clientX, startY: event.clientY, origin: view }
    }

    const handleMouseMove = (event) => {
        const drag = dragRef.current
        if (!drag) return
        setView({
            scale: drag.origin.scale,
            x: drag.origin.x + (event.clientX - drag.startX),
            y: drag.origin.y + (event.clientY - drag.startY),
        })
    }

    const endDrag = () => { dragRef.current = null }

    const handleClose = () => { imageViewer.value = null }

    // Arrows move, +/- zoom, 0 resets. Esc is MUI's own.
    useEffect(() => {
        if (!data) return undefined
        const onKey = (event) => {
            if (event.key === 'ArrowRight') go(1)
            if (event.key === 'ArrowLeft') go(-1)
            if (event.key === '+' || event.key === '=') zoomAt(1.25, { x: 0, y: 0 })
            if (event.key === '-') zoomAt(0.8, { x: 0, y: 0 })
            if (event.key === '0') setView(AT_REST)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [data, go, zoomAt])

    // Warm the neighbours: a move that has to fetch the file shows an empty
    // stage first, which reads as a stutter however smooth the animation is.
    useEffect(() => {
        if (!data || count < 2 || data.kind === 'video') return
        for (const delta of [1, -1]) {
            const image = new Image()
            image.src = urls[(index + delta + count) % count]
        }
    }, [data, index, count, urls])

    if (!data || !count) return null

    const current = urls[index]
    const isVideo = data.kind === 'video'

    return (
        <Modal open onClose={handleClose} sx={{ display: 'flex', p: 4 }}>
            <Box sx={{
                m: 'auto',
                width: '100%',
                maxWidth: 1100,
                // A definite height, not a max: the stage below is `flex: 1`
                // with an absolutely-positioned image inside, so nothing in it
                // has intrinsic height. With `maxHeight` the column collapsed
                // and the dialog rendered as a thin strip.
                height: '92vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#fff',
                borderRadius: 1,
                outline: 'none',
                overflow: 'hidden',
            }}>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 2, py: 1, borderBottom: '1px solid #e5e7eb', flexShrink: 0,
                }}>
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={data.title}
                    >
                        {data.title}
                    </Typography>

                    {count > 1 && (
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            {index + 1} / {count}
                        </Typography>
                    )}

                    {zoomed && !isVideo && (
                        <>
                            <Typography variant="caption" sx={{ color: '#6b7280', ml: 1 }}>
                                {Math.round(view.scale * 100)}%
                            </Typography>
                            <IconButton size="small" onClick={() => setView(AT_REST)} title="Fit to screen (0)">
                                <IconSeeker icon="compress" sx={{ fontSize: 14 }} />
                            </IconButton>
                        </>
                    )}

                    <IconButton size="small" component="a" href={current} target="_blank" rel="noreferrer" title="Open the original">
                        <IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton size="small" onClick={handleClose} title="Close">
                        <IconSeeker icon="xmark" sx={{ fontSize: 14 }} />
                    </IconButton>
                </Box>

                <Box
                    ref={stageRef}
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={endDrag}
                    onMouseLeave={endDrag}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        position: 'relative',
                        // Hidden, not auto: the image is moved by dragging it,
                        // and a scrollbar would fight the drag.
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f9fafb',
                        p: 1,
                        touchAction: 'none',
                        userSelect: 'none',
                    }}
                >
                    {count > 1 && !zoomed && (
                        <IconButton
                            onClick={() => go(-1)}
                            title="Previous (←)"
                            sx={{ position: 'absolute', left: 8, zIndex: 2, backgroundColor: '#fff', boxShadow: 1, '&:hover': { backgroundColor: '#fff' } }}
                        >
                            <IconSeeker icon="chevron-left" sx={{ fontSize: 16 }} />
                        </IconButton>
                    )}

                    {/* The stage keeps its size whatever the image is: the
                        layer fills it and the picture is `contain`ed inside.
                        Letting the image size the box was most of the "jump" —
                        every photo has a different aspect ratio, so the whole
                        stage resized and re-centred on each move. */}
                    <Box
                        key={index}
                        sx={{
                            position: 'absolute',
                            inset: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            ...(navigatedRef.current ? {
                                '@keyframes slideIn': {
                                    from: { opacity: 0, transform: `translateX(${direction * 56}px)` },
                                    to: { opacity: 1, transform: 'translateX(0)' },
                                },
                                animation: 'slideIn .3s cubic-bezier(.22,.61,.36,1)',
                            } : {}),
                        }}
                    >
                        {isVideo
                            ? <VideoPane url={current} />
                            : <ImageSeeker
                                src={current}
                                draggable={false}
                                onDoubleClick={() => setView(zoomed ? AT_REST : { scale: 2.5, x: 0, y: 0 })}
                                sx={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
                                    // No transition while dragging or wheeling,
                                    // or the image lags behind the pointer.
                                    transition: dragRef.current ? 'none' : 'transform .08s linear',
                                    cursor: zoomed ? (dragRef.current ? 'grabbing' : 'grab') : 'zoom-in',
                                }}
                            />}
                    </Box>

                    {count > 1 && !zoomed && (
                        <IconButton
                            onClick={() => go(1)}
                            title="Next (→)"
                            sx={{ position: 'absolute', right: 8, zIndex: 2, backgroundColor: '#fff', boxShadow: 1, '&:hover': { backgroundColor: '#fff' } }}
                        >
                            <IconSeeker icon="chevron-right" sx={{ fontSize: 16 }} />
                        </IconButton>
                    )}

                    <Typography
                        variant="caption"
                        sx={{
                            position: 'absolute', bottom: 6, right: 10, zIndex: 2,
                            color: '#9ca3af', pointerEvents: 'none',
                        }}
                    >
                        {isVideo
                            ? 'Videos open in a new tab if the browser cannot play them'
                            : zoomed ? 'Drag to move · double-click to fit' : 'Scroll to zoom · double-click to zoom'}
                    </Typography>
                </Box>

                {count > 1 && (
                    <Box sx={{
                        display: 'flex', gap: 0.75, p: 1, flexShrink: 0,
                        borderTop: '1px solid #e5e7eb', overflowX: 'auto',
                    }}>
                        {urls.map((url, i) => (
                            isVideo
                            ? <Box
                                key={`${url}-${i}`}
                                title={url}
                                onClick={() => {
                                    if (i === index) return
                                    navigatedRef.current = true
                                    setDirection(i > index ? 1 : -1)
                                    setIndex(i)
                                    setView(AT_REST)
                                }}
                                sx={{
                                    width: 48, height: 48, flexShrink: 0, cursor: 'pointer',
                                    borderRadius: 1, display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', backgroundColor: '#111827',
                                    border: i === index ? '2px solid #2563eb' : '1px solid #e5e7eb',
                                    opacity: i === index ? 1 : 0.65,
                                    '&:hover': { opacity: 1 },
                                }}
                            >
                                <IconSeeker icon="play" sx={{ fontSize: 14, color: '#fff' }} />
                            </Box>
                            : <ImageSeeker
                                key={`${url}-${i}`}
                                src={url}
                                onClick={() => {
                                    if (i === index) return
                                    navigatedRef.current = true
                                    setDirection(i > index ? 1 : -1)
                                    setIndex(i)
                                    setView(AT_REST)
                                }}
                                sx={{
                                    width: 48, height: 48, objectFit: 'contain', flexShrink: 0,
                                    cursor: 'pointer', borderRadius: 1,
                                    border: i === index ? '2px solid #2563eb' : '1px solid #e5e7eb',
                                    opacity: i === index ? 1 : 0.65,
                                    transition: 'opacity .2s ease',
                                    '&:hover': { opacity: 1 },
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Modal>
    )
}
